import * as THREE from 'three';
import { Tween, Easing, Group as TweenGroup } from '@tweenjs/tween.js';

class VisualEffectHelper {
  scene: THREE.Scene;
  tweenGroup: TweenGroup | null;
  particles: Array<{
    particleSystem: THREE.Points;
    particleGeometry: THREE.BufferGeometry;
    particleMaterial: THREE.PointsMaterial & { opacity: number };
    velocities: THREE.Vector3[];
    lifetime: number;
  }>;
  activeExplosions: number;
  maxExplosions: number;
  lastExplosionTime: number;
  explosionCooldown: number;
  _explosionPool: Array<{
    explosion: THREE.Mesh;
    explosionRing: THREE.Mesh;
    explosionLight: THREE.PointLight;
    radius: number;
  }>;

  constructor(scene: THREE.Scene, tweenGroup: TweenGroup | null = null) {
    this.scene = scene;
    this.tweenGroup = tweenGroup;
    this.particles = [];
    this.activeExplosions = 0;
    this.maxExplosions = 3;
    this.lastExplosionTime = 0;
    this.explosionCooldown = 100;
    this._explosionPool = [];
  }

  // Pre-create explosion instances to avoid runtime allocations
  initExplosionPool(count = 3, radius = 0.5) {
    for (let i = 0; i < count; i++) {
      const inst = this._buildExplosionInstance(radius);
      this._explosionPool.push(inst);
    }
  }

  // Warm up GPU pipeline by creating and briefly attaching pooled meshes
  warmup() {
    const inst = this._getExplosionInstance(0.5) || this._buildExplosionInstance(0.5);
    this.scene.add(inst.explosion);
    this.scene.add(inst.explosionRing);
    this.scene.add(inst.explosionLight);
    this.scene.remove(inst.explosion);
    this.scene.remove(inst.explosionRing);
    this.scene.remove(inst.explosionLight);
    this._releaseExplosionInstance(inst);
  }

  _buildExplosionInstance(radius = 0.5) {
    const explosionGeometry = new THREE.SphereGeometry(radius, 8, 8);
    const explosionMaterial = new THREE.MeshBasicMaterial({
      color: 0xff4400,
      transparent: true,
      opacity: 0.0,
    });
    const explosion = new THREE.Mesh(explosionGeometry, explosionMaterial);
    explosion.visible = false;

    const ringGeometry = new THREE.RingGeometry(radius * 0.5, radius * 1.2, 8);
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0xffaa00,
      transparent: true,
      opacity: 0.0,
      side: THREE.DoubleSide,
    });
    const explosionRing = new THREE.Mesh(ringGeometry, ringMaterial);
    explosionRing.rotation.x = -Math.PI / 2;
    explosionRing.visible = false;

    const explosionLight = new THREE.PointLight(0xffaa33, 0, 10, 1);
    explosionLight.visible = false;

    return { explosion, explosionRing, explosionLight, radius };
  }

  _getExplosionInstance(_radius = 0.5) {
    return this._explosionPool.pop() || null;
  }

  _releaseExplosionInstance(inst: ReturnType<VisualEffectHelper['_buildExplosionInstance']>) {
    if (!inst) return;
    inst.explosion.visible = false;
    (inst.explosion.material as THREE.Material & { opacity?: number }).opacity = 0.0 as any;
    inst.explosion.scale.set(1, 1, 1);
    inst.explosionRing.visible = false;
    (inst.explosionRing.material as THREE.Material & { opacity?: number }).opacity = 0.0 as any;
    inst.explosionRing.scale.set(1, 1, 1);
    inst.explosionLight.visible = false;
    inst.explosionLight.intensity = 0;
    this._explosionPool.push(inst);
  }

  // Create an explosion effect at the specified position
  createExplosion(position: THREE.Vector3, radius = 0.5) {
    const now = Date.now();
    if (now - this.lastExplosionTime < this.explosionCooldown) return;
    if (this.activeExplosions >= this.maxExplosions) return;

    this.lastExplosionTime = now;
    this.activeExplosions++;

    const inst = this._getExplosionInstance(radius) || this._buildExplosionInstance(radius);
    const { explosion, explosionRing, explosionLight } = inst;
    explosion.position.copy(position);
    explosionRing.position.copy(position);
    explosionLight.position.copy(position);
    explosion.visible = true;
    explosionRing.visible = true;
    explosionLight.visible = true;
    (explosion.material as any).opacity = 0.8;
    (explosionRing.material as any).opacity = 0.6;
    this.scene.add(explosion);
    this.scene.add(explosionRing);
    this.scene.add(explosionLight);

    const maxExplosionSize = radius * 3;
    const maxRingSize = radius * 2;

    const explosionTween = new (Tween as any)(explosion)
      .to(
        {
          scale: { x: maxExplosionSize, y: maxExplosionSize, z: maxExplosionSize },
          material: { opacity: 0 },
        },
        300
      )
      .easing(Easing.Exponential.Out)
      .onUpdate((obj: any) => {
        explosion.scale.set(obj.scale.x, obj.scale.y, obj.scale.z);
        (explosion.material as any).opacity = obj.material.opacity;
      })
      .onComplete(() => {
        this.scene.remove(explosion);
        this.scene.remove(explosionRing);
        this.scene.remove(explosionLight);
        this.activeExplosions--;
        this._releaseExplosionInstance(inst);
      });

    const ringTween = new (Tween as any)(explosionRing)
      .to(
        {
          scale: { x: maxRingSize, y: maxRingSize, z: maxRingSize },
          material: { opacity: 0 },
        },
        350
      )
      .easing(Easing.Exponential.Out)
      .onUpdate((obj: any) => {
        explosionRing.scale.set(obj.scale.x, obj.scale.y, obj.scale.z);
        (explosionRing.material as any).opacity = obj.material.opacity;
      });

    const lightTween = new (Tween as any)(explosionLight)
      .to({ intensity: 0 }, 250)
      .easing(Easing.Exponential.Out);

    if (this.tweenGroup) {
      this.tweenGroup.add(explosionTween as any);
      this.tweenGroup.add(ringTween as any);
      this.tweenGroup.add(lightTween as any);
    }
    explosionTween.start();
    ringTween.start();
    lightTween.start();
  }

  // Create a particle trail effect for a projectile
  createTrail(projectileMesh: THREE.Object3D) {
    const particleGeometry = new THREE.BufferGeometry();
    const particleCount = 15;
    const particles = new Float32Array(particleCount * 3);
    const lifetimes = new Float32Array(particleCount);
    const sizes = new Float32Array(particleCount);

    for (let i = 0; i < particleCount; i++) {
      particles[i * 3] = projectileMesh.position.x;
      particles[i * 3 + 1] = projectileMesh.position.y;
      particles[i * 3 + 2] = projectileMesh.position.z;
      lifetimes[i] = i / particleCount;
      sizes[i] = 0.2 - i * 0.01;
    }

    particleGeometry.setAttribute('position', new THREE.BufferAttribute(particles, 3));
    particleGeometry.setAttribute('lifetime', new THREE.BufferAttribute(lifetimes, 1));
    particleGeometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

    const particleMaterial = new THREE.PointsMaterial({
      color: 0xffffaa,
      size: 0.2,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true,
    });

    const particleTrail = new THREE.Points(particleGeometry, particleMaterial);
    this.scene.add(particleTrail);

    const updateTrail = (projectile: THREE.Object3D, deltaTime: number) => {
      const positions = particleGeometry.attributes.position.array as Float32Array;
      const lifetimesArr = particleGeometry.attributes.lifetime.array as Float32Array;
      const sizesArr = particleGeometry.attributes.size.array as Float32Array;

      for (let i = particleCount - 1; i > 0; i--) {
        positions[i * 3] = positions[(i - 1) * 3];
        positions[i * 3 + 1] = positions[(i - 1) * 3 + 1];
        positions[i * 3 + 2] = positions[(i - 1) * 3 + 2];
        lifetimesArr[i] = Math.max(lifetimesArr[i] - deltaTime * 0.6, 0);
        sizesArr[i] = Math.max(sizesArr[i] - deltaTime * 0.1, 0.05);
      }

      positions[0] = (projectile as any).position.x;
      positions[1] = (projectile as any).position.y;
      positions[2] = (projectile as any).position.z;
      lifetimesArr[0] = 1.0;
      sizesArr[0] = 0.2;

      const avgLifetime =
        Array.from(lifetimesArr).reduce((sum, val) => sum + val, 0) /
        particleCount;
      const colorIntensity = Math.floor(avgLifetime * 255);
      particleMaterial.color.setHex(0xffff00 + (colorIntensity << 8));

      particleMaterial.opacity = avgLifetime * 0.8;

      particleGeometry.attributes.position.needsUpdate = true;
      (particleGeometry.attributes as any).lifetime.needsUpdate = true;
      (particleGeometry.attributes as any).size.needsUpdate = true;

      if (avgLifetime <= 0.05) {
        this.scene.remove(particleTrail);
        particleGeometry.dispose();
        particleMaterial.dispose();
      }
    };

    return { particleTrail, updateTrail };
  }

  // Create impact effect when projectile hits target
  createImpact(position: THREE.Vector3, _normal: THREE.Vector3) {
    const sparkleCount = 8;
    const sparkleGeometry = new THREE.BufferGeometry();
    const sparklePositions = new Float32Array(sparkleCount * 3);
    const sparkleVelocities: THREE.Vector3[] = [];

    for (let i = 0; i < sparkleCount; i++) {
      sparklePositions[i * 3] = position.x + (Math.random() - 0.5) * 0.2;
      sparklePositions[i * 3 + 1] = position.y + (Math.random() - 0.5) * 0.2;
      sparklePositions[i * 3 + 2] = position.z + (Math.random() - 0.5) * 0.2;

      const velocity = new THREE.Vector3(
        (Math.random() - 0.5) * 2,
        Math.random() * 2,
        (Math.random() - 0.5) * 2
      );
      sparkleVelocities.push(velocity);
    }

    sparkleGeometry.setAttribute('position', new THREE.BufferAttribute(sparklePositions, 3));

    const sparkleMaterial = new THREE.PointsMaterial({
      color: 0xffff00,
      size: 0.05,
      transparent: true,
      opacity: 1.0,
      blending: THREE.AdditiveBlending,
    });

    const sparkles = new THREE.Points(sparkleGeometry, sparkleMaterial);
    this.scene.add(sparkles);

    const sparkleTweens: any[] = [];
    for (let i = 0; i < sparkleCount; i++) {
      const finalPosition = new THREE.Vector3(
        sparklePositions[i * 3] + sparkleVelocities[i].x,
        sparklePositions[i * 3 + 1] + sparkleVelocities[i].y,
        sparklePositions[i * 3 + 2] + sparkleVelocities[i].z
      );

      const sparkleTween = new Tween({
        x: sparklePositions[i * 3],
        y: sparklePositions[i * 3 + 1],
        z: sparklePositions[i * 3 + 2],
        opacity: 1.0,
      })
        .to(
          { x: finalPosition.x, y: finalPosition.y, z: finalPosition.z, opacity: 0 },
          300 + Math.random() * 200
        )
        .easing(Easing.Quadratic.Out)
        .onUpdate((coords: any) => {
          sparklePositions[i * 3] = coords.x;
          sparklePositions[i * 3 + 1] = coords.y;
          sparklePositions[i * 3 + 2] = coords.z;
          (sparkleGeometry.attributes.position as any).needsUpdate = true;
        })
        .onComplete(() => {
          if ((sparkleTweens as any).every((t: any) => t.isComplete)) {
            this.scene.remove(sparkles);
            sparkleGeometry.dispose();
            sparkleMaterial.dispose();
          }
        });

      sparkleTweens.push(sparkleTween);

      if (this.tweenGroup) {
        this.tweenGroup.add(sparkleTween as any);
      }
      sparkleTween.start();
    }

    const flashGeometry = new THREE.SphereGeometry(0.1, 8, 8);
    const flashMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.8,
    });
    const flash = new THREE.Mesh(flashGeometry, flashMaterial);
    flash.position.copy(position);
    this.scene.add(flash);

    const flashTween = new Tween(flash.scale)
      .to({ x: 2, y: 2, z: 2 }, 100)
      .easing(Easing.Exponential.Out);

    const flashOpacityTween = new Tween(flashMaterial)
      .to({ opacity: 0 }, 100)
      .easing(Easing.Exponential.Out)
      .onComplete(() => {
        this.scene.remove(flash);
        flashGeometry.dispose();
        flashMaterial.dispose();
      });

    if (this.tweenGroup) {
      this.tweenGroup.add(flashTween as any);
      this.tweenGroup.add(flashOpacityTween as any);
    }
    flashTween.start();
    flashOpacityTween.start();
  }

  // Update all particle systems over time
  update(deltaTime: number) {
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const particleObject = this.particles[i];
      const { particleSystem, particleGeometry, particleMaterial, velocities } = particleObject;
      const positions = particleGeometry.attributes.position.array as Float32Array;

      const speedMultiplier = deltaTime * 3;
      for (let j = 0; j < velocities.length; j++) {
        positions[j * 3] += velocities[j].x * speedMultiplier;
        positions[j * 3 + 1] += velocities[j].y * speedMultiplier;
        positions[j * 3 + 2] += velocities[j].z * speedMultiplier;
      }
      particleGeometry.attributes.position.needsUpdate = true;

      particleObject.lifetime -= deltaTime * 0.8;
      particleMaterial.opacity = particleObject.lifetime;

      if (particleMaterial.opacity <= 0) {
        this.scene.remove(particleSystem);
        particleGeometry.dispose();
        particleMaterial.dispose();
        this.particles.splice(i, 1);
      }
    }
  }
}

export default VisualEffectHelper;
